import React, {Component, MouseEvent, ReactNode} from 'react';
import SmartLabelTooltip from 'app/components/reusable/SmartLabelTooltip';
import Checkbox from 'app/components/auth/Checkbox';
import {IncubatorCreateLineController} from 'app/controllers/IncubatorCreateLineController';
import {ElementWithError} from 'app/components/reusable/ElementWithError';
import {inject, observer} from 'mobx-react';
import {IReactionDisposer, reaction} from 'mobx';
import I18n from 'app/i18n';
import {MIN_COEF_CHANGING, MAX_COEF_CHANGING, MIN_DELAY_VALUE, MAX_DELAY_VALUE} from 'app/utils/Consts';
import {shouldSyncIncubatorCreateFormFromPresets} from 'app/utils/shouldSyncIncubatorCreateFormFromPresets';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {UserStore} from 'app/store/UserStore';

const DEFAULT_DELAY_VALUE = 10;
const DEFAULT_COEFFICIENT_VALUE = 1.5;
const SECOND_INDEX_OF_ARRAY = 2;

interface Props {
    controller?: IncubatorCreateLineController
    isEditable: boolean
    updateSettings: boolean
    setUpdateSettings: () => void
    userStore?: UserStore
    params: { id?: number }
}
interface State {
    isOpenModal: boolean
    old_coef_changing: number,
    old_delay_for_bets_time: number,
    hideNickname: boolean,
    considerPersonalLimits: boolean,
    toSave: boolean
}

@inject('controller', 'userStore')
@observer
export default class IncubatorCreateSettings extends Component<Props, State> {
    state = {
        considerPersonalLimits: true,
        hideNickname: true,
        isOpenModal: false,
        old_coef_changing: -1,
        old_delay_for_bets_time: -1,
        toSave: false
    };

    presetsDisposer: IReactionDisposer;

    get controller(): IncubatorCreateLineController {
        return this.props.controller;
    }

    componentDidMount(): void {
        this.checkDelayValueNull();
        const useDefaultUserValue = !this.props.isEditable;
        this.syncInputsWithController(useDefaultUserValue);

        if (useDefaultUserValue) {
            this.applyOptionsToController();
        }

        window.addEventListener('beforeunload', this.saveSettingsBeforeUnload);
        this.startPresetsReaction();
    }

    componentDidUpdate(prevProps: Props): void {
        this.checkDelayValueNull();

        if (this.state.isOpenModal) {
            return;
        }

        if (this.props.isEditable) {
            const {coef_changing, delay_for_bets_time} = this.props.controller.incubatorGame.attributes;
            const {old_coef_changing, old_delay_for_bets_time} = this.state;

            if (old_coef_changing !== coef_changing || old_delay_for_bets_time !== delay_for_bets_time || this.props.updateSettings) {
                this.syncInputsWithController();
                this.props.setUpdateSettings();
            }
        } else if (prevProps.params?.id !== this.props.params?.id) {
            this.syncInputsWithController(true);
            this.applyOptionsToController();
            this.props.setUpdateSettings();

            if (this.state.toSave) {
                this.setState({toSave: false});
            }
        }
    }

    componentWillUnmount(): void {
        this.saveSettingsBeforeUnload();
        window.removeEventListener('beforeunload', this.saveSettingsBeforeUnload);
        this.presetsDisposer?.();
    }

    startPresetsReaction(): void {
        this.presetsDisposer = reaction(
            () => this.props.userStore.user.incubator_initial_presets,
            (nextPresets, previousPresets) => {
                if (!shouldSyncIncubatorCreateFormFromPresets({
                    isEditable: this.props.isEditable,
                    isOpenModal: this.state.isOpenModal,
                    nextPresets,
                    previousPresets
                })) {
                    return;
                }

                this.syncInputsWithController(true);
                this.applyOptionsToController();
            }
        );
    }

    syncInputsWithController(useDefaultUserValue = false): void {
        const {coef_changing, delay_for_bets_time, hide_creator, consider_personal_limits} = this.props.controller.incubatorGame.attributes;
        const {incubator_initial_presets} = this.props.userStore.user;
        let defaultDelayBets = null;
        let defaultCoefChanging = null;
        let defaultHidereator = null;
        let defaultConsiderPersonalLimits = null;

        if (useDefaultUserValue && incubator_initial_presets) {
            defaultDelayBets = incubator_initial_presets.delay_for_bets_time;
            defaultCoefChanging = incubator_initial_presets.coef_changing;
            defaultHidereator = incubator_initial_presets.hide_creator;
            defaultConsiderPersonalLimits = incubator_initial_presets.consider_personal_limits;
        }

        this.delayInputRef.current.value = `${defaultDelayBets ?? delay_for_bets_time}`;
        this.coefficientInputRef.current.value = `${defaultCoefChanging ?? coef_changing}`;
        this.setState({
            considerPersonalLimits: defaultConsiderPersonalLimits ?? consider_personal_limits,
            hideNickname: defaultHidereator ?? hide_creator,
            old_coef_changing: defaultCoefChanging ?? coef_changing,
            old_delay_for_bets_time: defaultDelayBets ?? delay_for_bets_time
        });
    }

    saveSettingsBeforeUnload = (): void => {
        if (this.state.toSave) {
            this.controller.incubatorGame.save(true);
        }
    };

    delayInputRef: React.RefObject<HTMLInputElement> = React.createRef();

    coefficientInputRef: React.RefObject<HTMLInputElement> = React.createRef();

    setOptionsByDefault = (e: MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        const {controller} = this.props;
        this.delayInputRef.current.value = `${DEFAULT_DELAY_VALUE}`;
        this.coefficientInputRef.current.value = `${DEFAULT_COEFFICIENT_VALUE}`;
        this.setState({
            considerPersonalLimits: true,
            hideNickname: true
        });
        controller.incubatorGame.clearError('coef_changing');
        controller.incubatorGame.clearError('delay_for_bets_time');
    };

    validationSettings = (): void => {
        const {incubatorGame} = this.controller;
        const delay_for_bets_time = Number(this.delayInputRef.current.value);
        const coef_changing = this.coefficientInputRef.current.value;

        if (delay_for_bets_time < MIN_DELAY_VALUE || delay_for_bets_time > MAX_DELAY_VALUE) {
            incubatorGame.setErrors({...incubatorGame.errors,
                delay_for_bets_time: [I18n.t('amount_range_error', {max_amount: MAX_DELAY_VALUE, min_amount: MIN_DELAY_VALUE})]});
        }
        if (Number(coef_changing) < MIN_COEF_CHANGING || Number(coef_changing) > MAX_COEF_CHANGING || coef_changing === '') {
            incubatorGame.setErrors({...incubatorGame.errors,
                coef_changing: [I18n.t('amount_range_error', {max_amount: MAX_COEF_CHANGING, min_amount: MIN_COEF_CHANGING})]});
        }
    };

    applyOptionsToController = (): boolean => {
        this.validationSettings();
        const {incubatorGame} = this.controller;

        if (incubatorGame.getError('delay_for_bets_time') || incubatorGame.getError('coef_changing')) {
            return false;
        }

        const delay_for_bets_time = Number(this.delayInputRef.current.value);
        const coef_changing = Number(this.coefficientInputRef.current.value);

        this.controller.onChangeDelayBet(delay_for_bets_time);
        this.controller.onChangeCoefChanging(coef_changing);
        this.controller.onChangeHideNickname(this.state.hideNickname);
        this.controller.onChangeConsiderPersonalLimits(this.state.considerPersonalLimits);
        this.setState({
            old_coef_changing: coef_changing,
            old_delay_for_bets_time: delay_for_bets_time
        });

        return true;
    };

    saveOptions = (e?: MouseEvent<HTMLButtonElement>): void => {
        e && e.preventDefault();

        if (!this.applyOptionsToController()) {
            return;
        }

        this.setState({isOpenModal: false});

        if (this.props.isEditable) {
            this.controller.incubatorGame.save(true)
                .then(() => this.setState({toSave: false}))
                .catch(() => this.setState({toSave: true}));
        }
    };

    checkDelayValueNull = (): void => {
        if (this.controller.incubatorGame.attributes.delay_for_bets_time === null) {
            const defaultUserDelay = this.props.userStore.user.incubator_initial_presets?.delay_for_bets_time;
            this.controller.onChangeDelayBet(defaultUserDelay ?? DEFAULT_DELAY_VALUE);
        }
    };

    handleDelayInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.target.value = e.target.value.replace(/[^0-9]/gu, '');
        this.controller.incubatorGame.clearError('delay_for_bets_time');
    };

    handleCoefficientInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        let inputValue = e.target.value.replace(/[^0-9.]/gu, '').replace(/(?<first>\..*?)\..*/gu, '$<first>');

        if (inputValue.length === 1 && inputValue.substring(0, 1) === '.') {
            inputValue = '';
        }

        if (inputValue.length > 1 && inputValue.substring(1, SECOND_INDEX_OF_ARRAY) !== '.') {
            inputValue = inputValue[0].concat('.', inputValue[1]);
        }

        if (Number(inputValue) >= MIN_COEF_CHANGING && Number(inputValue) <= MAX_COEF_CHANGING) {
            this.controller.incubatorGame.clearError('coef_changing');
        }
        e.target.value = inputValue;
    };

    handleHideNickname = (hideNickname: boolean): void => {
        this.setState({hideNickname});
    };

    handleConsiderPersonalLimits = (considerPersonalLimits: boolean): void => {
        this.setState({considerPersonalLimits});
    };

    onClick = (e: MouseEvent<HTMLButtonElement>):void => {
        e.preventDefault();
        this.setState({isOpenModal: !this.state.isOpenModal});
    };

    errorHint = (field: string): ReactNode => this.error(field);

    error = (field: string): string => this.controller.incubatorGame.getError(field);


    render(): ReactNode {
        const {coef_changing, delay_for_bets_time} = this.controller.incubatorGame.attributes;

        return <div className={`incubator-create-settings ${this.state.isOpenModal ? 'active' : '' }`}>
            <button
                className={`incubator-create-settings__button
                    ${this.error('coef_changing') || this.error('delay_for_bets_time') ? 'error' : ''}`}
                onClick={this.onClick}
                type="button"
            >
                <SpriteIcon name="#svg-settings" className="svg-default"/>
            </button>
            <div className="incubator-create-settings__modal">
                <form className="incubator-create-settings__form">
                    <div className="incubator-create-settings__form-row">
                        <SmartLabelTooltip
                            forLabelTooltip="acceptanceDelay"
                            labelTooltip="title_incubator_acceptance_delay"
                            descriptionTooltip="tooltip_incubator_acceptance_delay_1"
                            additionalDescriptionTooltip="tooltip_incubator_acceptance_delay_2"
                        />
                        <ElementWithError
                            errorHint={this.errorHint('delay_for_bets_time')}
                            className={this.error('delay_for_bets_time') ? 'field-error' : ''}
                        >
                            <input
                                id="acceptanceDelay"
                                className="field__input field-number__input"
                                placeholder={`${delay_for_bets_time}`}
                                maxLength={2}
                                name="acceptanceDelay"
                                onChange={this.handleDelayInput}
                                ref={this.delayInputRef}
                            />
                        </ElementWithError>
                    </div>
                    <div className="incubator-create-settings__form-row">
                        <SmartLabelTooltip
                            forLabelTooltip="changeCoefficientIncubator"
                            labelTooltip="title_incubator_change_coefficient"
                            descriptionTooltip="tooltip_incubator_change_coefficient_1"
                            additionalDescriptionTooltip="tooltip_incubator_change_coefficient_2"
                        />
                        <ElementWithError
                            errorHint={this.errorHint('coef_changing')}
                            className={this.error('coef_changing') ? 'field-error' : ''}
                        >
                            <input
                                id="changeCoefficientIncubator"
                                className="field__input field-number__input"
                                placeholder={`${coef_changing}`}
                                maxLength={4}
                                name="changeCoefficientIncubator"
                                onChange={this.handleCoefficientInput}
                                ref={this.coefficientInputRef}
                            />
                        </ElementWithError>
                    </div>
                    <div className="incubator-create-settings__form-row--without-label">
                        <Checkbox
                            label={I18n.t('tooltip_incubator_consider_personal_limits')}
                            onChange={this.handleConsiderPersonalLimits}
                            value={this.state.considerPersonalLimits}
                        />
                        <SmartLabelTooltip
                            labelTooltip="tooltip_incubator_hint_consider_personal_limits"
                            descriptionTooltip="tooltip_incubator_hint_consider_personal_limits"
                        />
                    </div>
                    <Checkbox
                        label={I18n.t('tooltip_incubator_hide_nickname')}
                        onChange={this.handleHideNickname}
                        value={this.state.hideNickname}
                    />
                    <button
                        className="incubator-create-settings__form-btn button-adaptive button-adaptive_full"
                        onClick={this.setOptionsByDefault}
                    >{I18n.t('by_default')}</button>
                    <button
                        className="incubator-create-settings__form-btn incubator-create-settings__form-btn--save button-adaptive button-adaptive_full"
                        onClick={this.saveOptions}
                    >{I18n.t('apply')}</button>
                </form>
            </div>
        </div>;
    }
}
